/**
 * One glyph per sidebar pane.
 *
 * The rail is the only thing naming the panes now, so each icon has to be
 * legible at 20px and, more importantly, unmistakable for its neighbours:
 * a compass, a rising line, two people, a hazard triangle, a thermometer, a
 * scatter and a euro. Stroked in `currentColor` so the active/idle colouring
 * is the button's business, not the icon's.
 */

type IconProps = { className?: string };

function Svg({ className, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={className}
    >
      {children}
    </svg>
  );
}

/** Explore — a compass: the pane you navigate from. */
export function CompassIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M15.2 8.8 13.6 13.6 8.8 15.2 10.4 10.4Z" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Scenario — a value carried forward. */
export function TrendIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M3.5 16.5 9 11l3.5 3.5L20.5 6.5" />
      <path d="M15.5 6.5h5v5" />
    </Svg>
  );
}

/** People — the census pane. */
export function PeopleIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <circle cx="9.5" cy="8.5" r="3.2" />
      <path d="M3.8 19.2c0-3.1 2.6-5 5.7-5s5.7 1.9 5.7 5" />
      <circle cx="17.4" cy="9.8" r="2.2" />
      <path d="M17 14.6c2.1 0 3.7 1.3 3.7 3.6" />
    </Svg>
  );
}

/** Risks — the hazard triangle, which is what this pane is about. */
export function HazardIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M12 4.2 21 19.6H3Z" />
      <path d="M12 10v4" />
      <path d="M12 17h.01" />
    </Svg>
  );
}

/** Climate — a thermometer; every indicator here is a temperature or follows one. */
export function ThermometerIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14 14.3V5.5a2 2 0 1 0-4 0v8.8a4 4 0 1 0 4 0Z" />
      <path d="M12 9.5v5.2" />
    </Svg>
  );
}

/** Stats — a scatter against two axes, which is the whole pane. */
export function ScatterIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M4 3.8v16.4h16.2" />
      <circle cx="8.6" cy="16.4" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="12.4" cy="12.2" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="15.6" cy="14.2" r="1.3" fill="currentColor" stroke="none" />
      <circle cx="18.6" cy="7.8" r="1.3" fill="currentColor" stroke="none" />
    </Svg>
  );
}

/** Cost — a euro sign, drawn rather than typed so it lines up with the others. */
export function EuroIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M17 7.6a5.6 5.6 0 0 0-8.6 4.4A5.6 5.6 0 0 0 17 16.4" />
      <path d="M5.6 10.6h7" />
      <path d="M5.6 13.6h7" />
    </Svg>
  );
}

/** Log out — the door, at the foot of the rail. */
export function LogoutIcon(props: IconProps) {
  return (
    <Svg {...props}>
      <path d="M14.5 4.5H6.2A1.7 1.7 0 0 0 4.5 6.2v11.6a1.7 1.7 0 0 0 1.7 1.7h8.3" />
      <path d="M16.5 8.5 20 12l-3.5 3.5" />
      <path d="M20 12h-9" />
    </Svg>
  );
}
